import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, Mail, Loader2, AlertCircle, KeyRound, ArrowLeft, LifeBuoy } from "lucide-react";
import { setAdminToken, getAdminToken } from "../utils/adminAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  // Step 1 = email/password; Step 2 = enter 6-digit TOTP; Step 3 = recovery code fallback.
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getAdminToken()) {
      navigate("/admin/publishers", { replace: true });
    }
  }, [navigate]);

  const performLogin = async (extra = {}) => {
    setError("");
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          ...extra,
        }),
      });
      let data = null;
      try { data = await resp.json(); } catch { /* ignore */ }
      if (!resp.ok) {
        const detail = data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(" ")
          : resp.status === 401
          ? "Invalid credentials."
          : `Login failed (${resp.status}).`;
        setError(msg);
        return;
      }
      // The backend returns { requires_2fa: true } when the password is correct
      // but a TOTP code is still needed. Switch the UI to the 2FA step.
      if (data?.requires_2fa) {
        setStep(2);
        setError("");
        return;
      }
      setAdminToken(data.access_token);
      navigate("/admin/publishers", { replace: true });
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStep1 = (e) => {
    e.preventDefault();
    performLogin();
  };

  const handleSubmitStep2 = (e) => {
    e.preventDefault();
    if (!/^[0-9]{6}$/.test(totpCode.trim())) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    performLogin({ totp_code: totpCode.trim() });
  };

  const handleSubmitStep3 = (e) => {
    e.preventDefault();
    if (!recoveryCode.trim()) {
      setError("Paste one of your recovery codes (e.g. ABCD-1234).");
      return;
    }
    performLogin({ recovery_code: recoveryCode.trim() });
  };

  const goBackToStep1 = () => {
    setStep(1);
    setTotpCode("");
    setRecoveryCode("");
    setError("");
  };

  return (
    <div
      className="admin-shell relative min-h-screen flex items-center justify-center px-4 bg-[#FAFAFA] overflow-hidden"
      data-testid="admin-login-page"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
        }}
      />
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-[6px] bg-slate-900" />

      <div className="relative w-full max-w-[420px]">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center shadow-sm">
            <Shield className="w-4.5 h-4.5" strokeWidth={2.25} />
          </div>
          <div>
            <div className="font-display font-bold text-[15px] text-slate-900 tracking-tight">
              DMM Control
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mt-0.5">
              Admin Console
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          {/* Heading varies per step so the user always knows where they are. */}
          {step === 1 && (
            <>
              <h2 className="font-display text-xl font-bold text-slate-900 tracking-tight">Sign in</h2>
              <p className="text-sm text-slate-500 mt-1 mb-6">
                Authorized personnel only. Sessions are logged.
              </p>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-display text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-slate-700" strokeWidth={2.25} />
                Two-factor authentication
              </h2>
              <p className="text-sm text-slate-500 mt-1 mb-6">
                Open your authenticator app and enter the 6-digit code for{" "}
                <span className="font-medium text-slate-700">{email}</span>.
              </p>
            </>
          )}
          {step === 3 && (
            <>
              <h2 className="font-display text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-slate-700" strokeWidth={2.25} />
                Use a recovery code
              </h2>
              <p className="text-sm text-slate-500 mt-1 mb-6">
                Each recovery code works <strong>once</strong>. We&rsquo;ll log you in and consume it.
              </p>
            </>
          )}

          {error && (
            <div
              className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="admin-login-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleSubmitStep1} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1.5 uppercase tracking-[0.14em]">
                  Admin Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
                  <input
                    type="email" autoComplete="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@digitalmastermedia.com"
                    className="w-full pl-9 pr-3 py-2.5 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition text-sm"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1.5 uppercase tracking-[0.14em]">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
                  <input
                    type="password" autoComplete="current-password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full pl-9 pr-3 py-2.5 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition text-sm"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full py-2.5 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition shadow-sm hover:shadow"
                data-testid="admin-login-submit"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Signing in…</> : "Sign in"}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmitStep2} className="space-y-4" data-testid="admin-2fa-form">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1.5 uppercase tracking-[0.14em]">
                  6-digit code
                </label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
                  required autoFocus maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123 456"
                  className="w-full text-center text-2xl tracking-[0.4em] font-mono py-3 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition"
                  data-testid="admin-totp-input"
                />
              </div>
              <button
                type="submit" disabled={loading || totpCode.length !== 6}
                className="w-full py-2.5 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition shadow-sm hover:shadow"
                data-testid="admin-2fa-submit"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Verifying…</> : "Verify & sign in"}
              </button>
              <div className="flex items-center justify-between pt-2 text-[12px]">
                <button type="button" onClick={goBackToStep1} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900" data-testid="back-to-step1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button type="button" onClick={() => { setStep(3); setError(""); }} className="text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="use-recovery-code-btn">
                  Use a recovery code
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmitStep3} className="space-y-4" data-testid="admin-recovery-form">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1.5 uppercase tracking-[0.14em]">
                  Recovery code
                </label>
                <input
                  type="text" autoFocus required value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="ABCD-1234"
                  className="w-full text-center text-lg tracking-[0.2em] font-mono py-3 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition"
                  data-testid="admin-recovery-input"
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full py-2.5 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2 transition shadow-sm hover:shadow"
                data-testid="admin-recovery-submit"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Verifying…</> : "Sign in with recovery code"}
              </button>
              <div className="flex items-center justify-between pt-2 text-[12px]">
                <button type="button" onClick={() => { setStep(2); setError(""); }} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to code
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-500 mt-6">
          Access restricted. Unauthorized attempts are logged and reviewed.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
